import { memo, useEffect, useRef, useState } from 'react'

import { IconButton } from '@/components/ui/IconButton'
import { MarkdownMessage } from './MarkdownMessage'

interface ReasoningPanelProps {
  reasoning: string
  /**
   * Whether the associated assistant message is currently streaming.
   * Used to auto-expand while streaming and auto-collapse when done.
   */
  isStreaming?: boolean
}

export const ReasoningPanel = memo(({ reasoning, isStreaming }: ReasoningPanelProps) => {
  // Always start collapsed (including while streaming); the user can expand manually.
  const [isCollapsed, setIsCollapsed] = useState(true)
  const contentRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to bottom as reasoning grows, Cursor-style.
  useEffect(() => {
    if (isCollapsed) return
    const el = contentRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [reasoning, isCollapsed, contentRef])

  if (!reasoning || !reasoning.trim()) return null

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsCollapsed((prev) => !prev)
  }

  return (
    <div className={`mp-agent-reasoning-panel ${isCollapsed ? 'mp-agent-reasoning-collapsed' : ''}`}>
      <div className='mp-agent-reasoning-header'>
        <span className={`mp-agent-reasoning-label ${isStreaming ? 'mp-agent-reasoning-label-streaming' : ''}`}>
          Thinking
        </span>
        <IconButton icon={isCollapsed ? 'ChevronDown' : 'ChevronUp'} size='sm' onClick={handleToggle} />
      </div>
      {!isCollapsed && (
        <div className='mp-agent-reasoning-body'>
          <div className='mp-agent-reasoning-content' ref={contentRef}>
            <MarkdownMessage text={reasoning} className='mp-agent-message-markdown mp-agent-reasoning-markdown' />
          </div>
        </div>
      )}
    </div>
  )
})

ReasoningPanel.displayName = 'ReasoningPanel'
